import React, { useEffect, useMemo, useState } from 'react';
import { Dictionary, groupBy, isEqual, orderBy } from 'lodash';
import Radio, { RadioChangeEvent } from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Empty from 'antd/lib/empty';
import Tooltip from 'antd/lib/tooltip';
import { theme } from 'antd/lib';
import { usePrevious } from '../../usePrevious';
import { fetchSites } from '../../../domain/site';
import { ISite } from '../../../domain/site/interface';
import { PRICE_RESPONSE_MARKETS_DATA } from '../../../domain/price-response/interface';
import { toAllowedStringLength } from '../../../domain/common/formattersToAllowedValueLength';
import './price-response-target-sites-selector.css';
import { marketLoadZoneToLabel } from '../../site/SiteMarketAndLoadZone';

interface ISelectOption {
    value: string;
    label: string | React.ReactNode;
    sites: ISite[];
    enrolledSitesCount: number;
    lmp_market?: string;
    site_load_zone?: string;
}

const NOT_ENROLLED_COLOR = 'rgba(0, 0, 0, 0.45)';
const NOT_ENROLLED_SITE_TOOLTIP = 'Not enrolled to Price Response, this site will not be dispatched by the Price Response trigger.';
const NOT_ENROLLED_GROUP_TOOLTIP =
    'None of the sites in this market / load zone are enrolled to Price Response, the trigger will not dispatch any of them.';

const PRICE_RESPONSE_MARKETS = Object.keys(PRICE_RESPONSE_MARKETS_DATA);
const PRICE_RESPONSE_LOAD_ZONES = Object.values(PRICE_RESPONSE_MARKETS_DATA).flat(1);

interface IPriceResponseTargetSitesSelectorProps {
    companyId: number;
    marketAndZone: string[];
    onChange: Function;
    hasError: boolean;
}

export const PriceResponseTargetSitesSelector: React.FC<IPriceResponseTargetSitesSelectorProps> = ({
    companyId,
    marketAndZone,
    onChange,
    hasError,
}) => {
    const { token: themeColors } = theme.useToken();

    const prevValue = usePrevious(marketAndZone);
    const [selectedValue, setSelectedValue] = useState<string | null>(null);

    const { options, loading } = usePriceResponseGroupedSites({ companyId });
    const sortedOptions = useMemo(
        () =>
            orderBy(
                options,
                [
                    option => option.value === selectedValue, // selected value first
                    option => option.enrolledSitesCount, // then sort by number of sites enrolled to Price Response, groups without enrolled sites go last
                    option => option.value, // then sort alphabetical by value
                ],
                ['desc', 'desc', 'asc']
            ),
        [selectedValue, options]
    );

    const handleChange = (e: RadioChangeEvent) => {
        const value = e.target.value;
        const selectedOption = options.find(option => option.value === value);

        setSelectedValue(value);
        if (!selectedOption) {
            onChange([]);
        }

        onChange([selectedOption!.lmp_market!.toUpperCase(), selectedOption!.site_load_zone]);
    };

    useEffect(() => {
        if (!isEqual(prevValue, marketAndZone)) {
            const newValue = marketAndZone.length === 0 ? null : marketAndZone.join(' / ');
            setSelectedValue(newValue);
        }
    }, [prevValue, marketAndZone]);

    if (loading) {
        return (
            <div className="price-response-target-sites-selector loader-wrapper">
                <Spin spinning size="large" />
            </div>
        );
    }

    if (sortedOptions.length === 0) {
        return <Empty description="No sites enrolled to Price Response" />;
    }

    return (
        <Radio.Group onChange={handleChange} value={selectedValue} className="price-response-target-sites-selector wrapper">
            <div className="price-response-target-sites-selector radio-group-options-wrapper" style={{ borderColor: hasError ? 'red' : '#d9d9d9' }}>
                {sortedOptions.map(option => (
                    <Radio
                        key={option.value}
                        value={option.value}
                        className="price-response-target-sites-selector radio-item"
                        style={{
                            border: `1px solid ${option.value === selectedValue ? themeColors.colorPrimary : '#d9d9d9'}`,
                        }}
                    >
                        {option.label}
                    </Radio>
                ))}
            </div>
        </Radio.Group>
    );
};

function usePriceResponseGroupedSites({ companyId }: { companyId?: number }): {
    options: ISelectOption[];
    loading: boolean;
} {
    const [options, setOptions] = useState<ISelectOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        (async () => {
            setLoading(true);

            const { data } = await fetchSites({
                pagination: { pageSize: 10000, current: 1 },
                company_id: companyId,
            });

            // Filter out sites without market or zone
            let sites: ISite[] = data.filter(site => site.lmp_market && site.site_load_zone);

            // Filter out sites from not supported markets or zones
            sites = sites.filter(
                site =>
                    PRICE_RESPONSE_MARKETS.includes(site.lmp_market?.toUpperCase()!) &&
                    PRICE_RESPONSE_LOAD_ZONES.includes(site.site_load_zone.toUpperCase())
            );

            // Sites grouped by market/zone
            const result: Dictionary<ISite[]> = groupBy(sites, site =>
                [site.lmp_market?.toUpperCase(), marketLoadZoneToLabel(site.lmp_market, site.site_load_zone)].join(' / ')
            );
            const sortedMarketZones = orderBy(Object.keys(result), [record => result[record].length, record => record.toString()], ['desc', 'desc']);

            const newOptions = sortedMarketZones.map(groupName => transformSiteGroupToOption(groupName, result));

            setOptions(newOptions);
            setLoading(false);
        })();
    }, [companyId]);

    return {
        loading,
        options,
    };
}

function displaySiteNames(sites: ISite[]) {
    return sites.slice(0, 5).map((site, index) => (
        <span
            key={index}
            style={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: site.site_price_response_enrolled ? undefined : NOT_ENROLLED_COLOR,
            }}
        >
            <Tooltip
                title={site.site_price_response_enrolled ? site.site_name : `${site.site_name} - ${NOT_ENROLLED_SITE_TOOLTIP}`}
                placement="right"
            >
                {index + 1}. {toAllowedStringLength(site.site_name, 18)}
            </Tooltip>
        </span>
    ));
}

function transformSiteGroupToOption(groupName: string, sitesDict: Dictionary<ISite[]>) {
    const site = sitesDict[groupName][0];
    // Sites enrolled to Price Response first, so the preview shows the sites the trigger will actually dispatch
    const sites: ISite[] = orderBy(sitesDict[groupName], groupSite => groupSite.site_price_response_enrolled, 'desc');
    const sitesLength = sites.length;
    const enrolledSitesCount = sites.filter(groupSite => groupSite.site_price_response_enrolled).length;
    const hasEnrolledSites = enrolledSitesCount > 0;

    return {
        value: groupName,
        label: (
            <>
                <Tooltip title={hasEnrolledSites ? undefined : NOT_ENROLLED_GROUP_TOOLTIP} placement="right">
                    <div>
                        <b style={{ color: hasEnrolledSites ? undefined : NOT_ENROLLED_COLOR }}>{groupName}</b>
                        <br />
                        <small style={{ color: NOT_ENROLLED_COLOR }}>
                            {enrolledSitesCount} of {sitesLength} {sitesLength === 1 ? 'site' : 'sites'} enrolled
                        </small>
                    </div>
                </Tooltip>
                <div>
                    {displaySiteNames(sites)}
                    <small>{sitesLength > 5 ? `And ${sitesLength - 5} more...` : ''}</small>
                </div>
            </>
        ),
        sites,
        enrolledSitesCount,
        lmp_market: site?.lmp_market,
        site_load_zone: site?.site_load_zone,
    };
}
